// renderRouting.mjs
//
// Pure zone-routing helpers for the headless RENDER_BATCH (F1). No browser, no
// React — unit-tested by scripts/test-render-routing.mjs (node --test), mirroring
// the videoLib.mjs pattern.
//
// GROUP-ROUTING LAW (owner, 2026-07-31). A design routed to a specific TONE group
// (a studio group whose NAME matches /dark/i or /light/i) renders that group's
// zones PLUS every NEUTRAL group's zones. A NEUTRAL group is tone-independent BY
// DEFINITION — its name matches NEITHER /dark/i NOR /light/i (size charts, returns
// banners, flat-lays) — so it ALWAYS binds regardless of which tone routed the
// design. This closes the hole where a chart/banner living in a neutral group
// rendered ZERO times under pair tone-routing (the routed tone group excluded it),
// so an AUTO pair run landed 8 photos. Applies to PAIRS, SINGLES, AUTO and REVIEW —
// the binding gate is the one place all four flow through.
//
// Note (consequence of the /dark/i-vs-/light/i definition, owner-chosen): a group
// named after a garment COLOUR ("Black" / "Ivory") — not containing "dark"/"light" —
// counts as NEUTRAL here and will always bind. Pair routing (the suite's
// groupIdForClass) already keys on /dark/i,/light/i, so colour-named groups are not
// routing targets; if a set uses colour names AS its tone groups, revisit this axis.

// Once-per-listing chart-slot detector. Aligned VERBATIM with the suite's
// ONCE_PER_LISTING_SLOT (creative-seller-suite renderBatch.js) so the two repos
// agree on what a chart is: match slot-INTENT, not incidental substrings
// ("Oversized" / "Style Guide" / "Returns Guide" are NOT charts). Divergence here
// mis-binds a banner as an F8 inverse/combined chart — its own mangling bug.
export const CHART_SLOT_RE = /\bchart\b|\bsize\s*(?:chart|guide)\b|\bsizing\b/i;
export function isChartSlot(name) { return !!name && CHART_SLOT_RE.test(name); }

// A group is NEUTRAL (tone-independent → always binds) when its name matches NEITHER
// /dark/i NOR /light/i — the SAME axis the suite routes pairs on (classGroupName).
export function isNeutralGroupName(name) {
  return !/dark/i.test(name || "") && !/light/i.test(name || "");
}

// Should a template ZONE (smartObject) bind this design's src?
//   • no group on the zone           → never (unroutable).
//   • a PAIRED chart (pair truthy)   → always (F8 binds every grouped cell; tone
//     routing decides the ARTWORK per cell downstream, not whether it binds).
//   • route-all (grp null / "all")   → always (a group-less / all-groups design).
//   • the zone's group IS the routed one (soGroup === grp) → binds.
//   • the zone's group is NEUTRAL    → ALWAYS binds (the tone-independent law).
//   • otherwise (opposite tone group) → skip.
//
// GROUP TONE TAG (owner, 2026): `groupTone` is the group's explicit tag — "dark" /
// "light" (a TONE group, never neutral) or "all" (tone-independent → neutral). When
// PRESENT it WINS over the name regex: routing must never depend on what a member
// named a group. UNTAGGED (groupTone undefined/null) → the /dark/i,/light/i name
// heuristic below, byte-identical to the pre-tag behaviour.
// `groupName` is the zone group's resolved name; `groupResolved` false (group not
// found in the defs) keeps the strict routed-only behaviour rather than force-binding
// an unknown group.
export function shouldBindZone({ soGroup, grp, routeAll, pair, groupName, groupResolved = true, groupTone }) {
  if (!soGroup) return false;
  if (pair) return true;
  if (routeAll) return true;
  if (soGroup === grp) return true;
  // Explicit tag wins: "all" is neutral (always binds); "dark"/"light" is a tone
  // group, so it binds ONLY as the routed one (handled above) → here it skips.
  if (groupTone === "all") return true;
  if (groupTone === "dark" || groupTone === "light") return false;
  if (groupResolved && isNeutralGroupName(groupName)) return true;
  return false;
}

// DIRECT GROUP SEMANTICS (owner law, final): a GROUP's name names WHICH DESIGN goes
// there — "dark"→darkSrc, "light"→lightSrc, null (neutral)→combined. NO garment
// inversion on groups, EVER. `tone` is the caller's classification of the group name
// (studio's classifyGarmentTone: lexicon incl. /dark|light/). Used for the manual
// pair set's per-group gdm (garment mockups).
export function groupDesignArt(tone, { darkSrc, lightSrc, combined }) {
  return tone === "dark" ? darkSrc : tone === "light" ? lightSrc : combined;
}

// MEMBER-KEYED pair routing: each of a pair's two members carries a routing CHOICE —
// "auto" | "all" | groupId — from the dropdown under it on the pair card. Resolve ONE
// group's art from BOTH choices. A member CLAIMS a group when its choice is "all", is
// that exact groupId, or is "auto" and the group's direct tone matches the member (dark
// member ↔ dark-tone groups, light ↔ light) or the group is NEUTRAL (autoTone null →
// both auto-claim). Both claim → combined (neutral → per-member downstream). Only one →
// that member's src. Neither → null (UNCOVERED: this group gets no design from this
// pair, exactly like an unpaired design leaving a group empty).
export function pairGroupArt({ groupId, autoTone, darkChoice, lightChoice }, { darkSrc, lightSrc, combined }) {
  const claims = (choice, ownTone) =>
    choice === "all" || choice === groupId || (choice === "auto" && (autoTone === ownTone || autoTone == null));
  const d = claims(darkChoice, "dark");
  const l = claims(lightChoice, "light");
  if (d && l) return combined;
  if (d) return darkSrc;
  if (l) return lightSrc;
  return null; // uncovered
}

// CHART CELL art (F8) — the ONE place inversion lives, because a chart CELL names an
// actual GARMENT: a dark-garment cell shows the LIGHT design, a light-garment cell the
// DARK design (print-file law). A cell named after the DESIGN (/design/i) binds that
// design DIRECTLY; a neutral/unclassable cell shows the never-blank combined. Mirrors
// the RENDER_BATCH F8 mapping, per cell — independent of the group's direct semantics.
export function chartCellArt(tone, isDesignNamed, { darkSrc, lightSrc, combined }) {
  if (tone == null) return combined;
  if (isDesignNamed) return tone === "dark" ? darkSrc : lightSrc;   // design-named → direct
  return tone === "dark" ? lightSrc : darkSrc;                       // garment-named → inverse
}
